import { Button } from "@msuite/ui/button";
import { useDisclosure } from "@msuite/ui/hooks";
import { useGuardedDefaultValues } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal } from "@msuite/ui/modal";
import { invalidate, type RefetchFn, useQueryClient } from "@msuite/ui/query";
import { SegmentedControl } from "@msuite/ui/segmented-control";
import { responseHandler } from "@msuite/ui/utils";
import { type ProjectSchemaForm, projectSchema } from "@msuite/utils/schemas";
import React, { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { ProjectForm, useProjectMemberController } from "@/forms/project-form";
import { ProjectTicketsForm } from "@/forms/project-form/project-tickets-form";
import { useProjectTicketsController } from "@/forms/project-form/use-project-tickets-controller";
import { client } from "@/hono";
import { useTicketRules } from "@/hooks/use-ticket-rules";
import { AddTicketModal } from "../add-ticket-modal";
import { ModalLoadFailed } from "@/components/modals";

/** Props Interface */
interface EditProjectModalProps {
	isOpen: boolean;
	onClose: () => void;
	projectId: string;
	onComplete: (() => Promise<void> | void) | RefetchFn;
	initialView?: View;
}

type View = "general" | "tickets";

export function EditProjectModal({
	isOpen,
	onClose,
	projectId,
	onComplete,
	initialView = "general",
}: EditProjectModalProps) {
	const [currentView, setCurrentView] = useState<View>(initialView);

	/** Form */
	const { defaultValues, loadFailed } = useGuardedDefaultValues(async () => {
		const response = await client.projects[":projectId"].$get({
			param: { projectId },
		});
		const data = await responseHandler(response);
		return (data.project ?? {}) as ProjectSchemaForm;
	});
	const methods = useForm({
		defaultValues,
		resolver: resolver(projectSchema),
		reValidateMode: "onSubmit",
	});
	const projectMembers = useProjectMemberController({ projectId });
	const projectTickets = useProjectTicketsController({ projectId });

	/** Hooks */
	const addTicketModal = useDisclosure();
	const { t } = useTranslation();
	const { createIsDenied } = useTicketRules();
	const qc = useQueryClient();

	/** Functions */
	async function handleOnSubmit(json: ProjectSchemaForm) {
		try {
			const response = await client.projects[":projectId"].$patch({
				param: { projectId },
				json,
			});
			const data = await responseHandler(response);
			if (!data?.project) return;
			await projectTickets.handleOnSubmit(data.project.id);
			await projectMembers.handleOnSubmit(data.project.id);
			await qc.invalidateQueries({
				predicate: invalidate([projectId]),
			});
			await onComplete();
			handleOnClose();
		} catch (error) {
			console.error(error);
		}
	}

	function handleOnClose() {
		onClose();
		methods.reset();
	}

	/** Render */
	return (
		<React.Fragment>
			<FormProvider {...methods}>
				<Modal
					isOpen={isOpen}
					onClose={handleOnClose}
					title={t("projects.edit_project")}
					isLoading={methods.formState.isLoading && !loadFailed}
					onSubmit={methods.handleSubmit(handleOnSubmit)}
					width={currentView === "tickets" ? "700px" : undefined}
				>
					{loadFailed ? (
						<ModalLoadFailed onClose={handleOnClose} />
					) : (
						<>
							<Modal.Body className="p-0">
								<div className="px-6 pt-4 h-stack justify-between">
									<SegmentedControl
										value={currentView}
										onChange={setCurrentView}
										items={
											[
												{
													label: t("projects.project"),
													value: "general",
													cyId: "project-modal-general-tab",
												},
												{
													label: t("projects.tickets"),
													value: "tickets",
													cyId: "project-modal-tickets-tab",
												},
											] as const
										}
									/>
									{currentView === "tickets" && !createIsDenied(projectId) && (
										<Button
											icon={<Icon name="Plus" />}
											onClick={addTicketModal.onOpen}
											data-cy="project-modal-add-ticket-button"
										>
											{t("projects.add_ticket")}
										</Button>
									)}
								</div>
								{currentView === "general" && (
									<div className="v-stack gap-4 overflow-y-auto overflow-x-hidden pb-6 px-6 pt-0">
										<ProjectForm projectMemberController={projectMembers.controller} />
									</div>
								)}
								{currentView === "tickets" && (
									<div className="v-stack gap-4 overflow-y-auto overflow-x-hidden pb-6 px-6 pt-0">
										<ProjectTicketsForm
											projectTicketsController={projectTickets.controller}
											disclosure={addTicketModal}
											projectId={projectId}
										/>
									</div>
								)}
							</Modal.Body>
							<Modal.FooterForm submitLabel={t("general.save")} />
						</>
					)}
				</Modal>
			</FormProvider>
			{addTicketModal.isOpen && (
				<AddTicketModal
					onClose={addTicketModal.onClose}
					isOpen={addTicketModal.isOpen}
					projectId={null}
					withProjectSearch={false}
					onComplete={async (ticket, uploadResults) => {
						projectTickets.controller.addNewTicket({
							...ticket,
							uploadResults,
						});
					}}
				/>
			)}
		</React.Fragment>
	);
}
